/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  GetMCPSetupDocsRequest,
  GetMCPSetupDocsSecurity,
} from "../models/operations/getmcpsetupdocs.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildGetMCPSetupDocsQuery,
  GetMCPSetupDocsQueryData,
  prefetchGetMCPSetupDocs,
  queryKeyGetMCPSetupDocs,
} from "./getMCPSetupDocs.core.js";
export {
  buildGetMCPSetupDocsQuery,
  type GetMCPSetupDocsQueryData,
  prefetchGetMCPSetupDocs,
  queryKeyGetMCPSetupDocs,
};

export type GetMCPSetupDocsQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * getSetupDocs mcpRegistries
 *
 * @remarks
 * Get the published setup documentation for an upstream MCP server, located by endpoint URL and/or registry identifier
 */
export function useGetMCPSetupDocs(
  request?: GetMCPSetupDocsRequest | undefined,
  security?: GetMCPSetupDocsSecurity | undefined,
  options?: QueryHookOptions<
    GetMCPSetupDocsQueryData,
    GetMCPSetupDocsQueryError
  >,
): UseQueryResult<GetMCPSetupDocsQueryData, GetMCPSetupDocsQueryError> {
  const client = useGramContext();
  return useQuery({
    ...buildGetMCPSetupDocsQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * getSetupDocs mcpRegistries
 *
 * @remarks
 * Get the published setup documentation for an upstream MCP server, located by endpoint URL and/or registry identifier
 */
export function useGetMCPSetupDocsSuspense(
  request?: GetMCPSetupDocsRequest | undefined,
  security?: GetMCPSetupDocsSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    GetMCPSetupDocsQueryData,
    GetMCPSetupDocsQueryError
  >,
): UseSuspenseQueryResult<GetMCPSetupDocsQueryData, GetMCPSetupDocsQueryError> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildGetMCPSetupDocsQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setGetMCPSetupDocsData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      serverUrl?: string | undefined;
      registrySpecifier?: string | undefined;
      gramSession?: string | undefined;
      gramKey?: string | undefined;
      gramProject?: string | undefined;
    },
  ],
  data: GetMCPSetupDocsQueryData,
): GetMCPSetupDocsQueryData | undefined {
  const key = queryKeyGetMCPSetupDocs(...queryKeyBase);

  return client.setQueryData<GetMCPSetupDocsQueryData>(key, data);
}

export function invalidateGetMCPSetupDocs(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      serverUrl?: string | undefined;
      registrySpecifier?: string | undefined;
      gramSession?: string | undefined;
      gramKey?: string | undefined;
      gramProject?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: [
      "@gram/client",
      "mcpRegistries",
      "getSetupDocs",
      ...queryKeyBase,
    ],
  });
}

export function invalidateAllGetMCPSetupDocs(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "mcpRegistries", "getSetupDocs"],
  });
}
